import { useState, useCallback, useRef, useEffect } from 'react';

interface UseResizeOptions {
    initialWidth: number;
    minWidth: number;
    maxWidth?: number;
    onResize?: (width: number) => void;
    onMinThreshold?: () => void;
    minThreshold?: number;
    invertDirection?: boolean; // For right-side panels, invert the resize direction
}

export const useResize = ({
    initialWidth,
    minWidth,
    maxWidth,
    onResize,
    onMinThreshold,
    minThreshold,
    invertDirection = false
}: UseResizeOptions) => {
    const [width, setWidth] = useState(initialWidth);
    const [isResizing, setIsResizing] = useState(false);
    const startXRef = useRef<number>(0);
    const startWidthRef = useRef<number>(initialWidth);
    const previousWidthRef = useRef<number>(initialWidth);

    // Sync with external width changes
    useEffect(() => {
        if (!isResizing) {
            setWidth(initialWidth);
            previousWidthRef.current = initialWidth;
            startWidthRef.current = initialWidth;
        }
    }, [initialWidth, isResizing]);

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsResizing(true);
        startXRef.current = e.clientX;
        startWidthRef.current = width;
        previousWidthRef.current = width;
    }, [width]);

    useEffect(() => {
        if (!isResizing) return;

        const handleMouseMove = (e: MouseEvent) => {
            const deltaX = e.clientX - startXRef.current;
            const adjustedDelta = invertDirection ? -deltaX : deltaX;
            const newWidth = Math.max(minWidth, Math.min(maxWidth || Infinity, startWidthRef.current + adjustedDelta));

            setWidth(newWidth);
            onResize?.(newWidth);

            // Check if we've crossed the min threshold
            if (minThreshold && onMinThreshold && newWidth <= minThreshold && previousWidthRef.current > minThreshold) {
                onMinThreshold();
            }
            previousWidthRef.current = newWidth;
        };

        const handleMouseUp = () => {
            setIsResizing(false);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizing, minWidth, maxWidth, onResize, onMinThreshold, minThreshold]);

    return {
        width,
        isResizing,
        handleMouseDown,
        setWidth
    };
};

